import { TGlobalContainerDesigns } from '@styles/design-system/global-components/global-components.types';
import Container from '@ui/container/container';
import styles from './theme-pack-secondary-color-type.module.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Popover from '@ui/popover/popover';
import ColorPickerSimple from '@ui/color-picker/color-picker-simple';
import chroma from 'chroma-js';
import Text from '@ui/text/text/text';

interface Props {
  colorArray: [string, number];
  label: string;
  onChange: (newHex: string, newAlpha: number) => void;
}
const ThemePackSecondaryColorComponent = ({ colorArray, label, onChange }: Props) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastValidInput = useRef(colorArray[0].replace('#', '').toUpperCase());
  const [isOpen, setIsOpen] = useState(false);
  const [color, setColor] = useState(colorArray[0].replace('#', '').toUpperCase());

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = colorArray[0].replace('#', '').toUpperCase();
    }
    lastValidInput.current = colorArray[0].replace('#', '').toUpperCase();
  }, [colorArray]);

  const handleBlur = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = lastValidInput.current;
    }
  }, []);

  const handleHexInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const hex = e.target.value;
      if (hex.length === 6) {
        const int = parseInt(`0x${hex}`, 16);
        if (isNaN(int)) {
          console.warn('Bad input');
          return;
        }

        const color = chroma(hex);
        color.alpha(colorArray[1]);
        setColor(color.hex());
        onChange(color.hex(), color.alpha());
        lastValidInput.current = color.hex().replace('#', '').toUpperCase();
        if (inputRef.current) inputRef.current.value = color.hex().replace('#', '').toUpperCase();
      }
    },
    [colorArray, onChange]
  );

  return (
    <Container
      design={TGlobalContainerDesigns.GlobalCont2}
      className="flex ai-center gap-6 padding-h-6 jc-space-between"
      hoverBackground="var(--gray-800)"
      borderRadius="100px"
      minWidth="188px"
      height="32px"
      boxShadow={isOpen ? '0 0 0 1px var(--gray-100)' : 'none'}
    >
      <div className="flex ai-center gap-6">
        <Popover
          forcePosition="top-right"
          ref={popoverRef}
          width="232px"
          height="276px"
          overflow="hidden"
          contentClassName="position-fixed"
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          renderTriggerButton={(props) => (
            <div
              className="square-20 radius-full"
              style={{
                backgroundColor: colorArray[0],
                border: props.open ? '1px solid white' : '1px solid transparent',
              }}
            ></div>
          )}
        >
          <ColorPickerSimple
            value={colorArray[0]}
            onChange={(color) => {
              onChange(color.hex, color.opacity);
            }}
            boxShadow="0 2px 4px 2px rgba(0,0,0,.25), 0 8px 24px rgba(0,0,0,.25)"
          />
        </Popover>
        <Text tag="label" whiteSpace="nowrap" className="C1">
          {label}
        </Text>
      </div>
      <div className="flex ai-center jc-space-between">
        <span className="C1" style={{ color: 'var(--global-ico-text-2)' }}>
          #
        </span>
        <input
          type="text"
          placeholder={color}
          onChange={handleHexInputChange}
          className={`C1 flex-1 ${styles.resizingInput}`}
          defaultValue={color}
          ref={inputRef}
          onBlur={handleBlur}
        />
      </div>
    </Container>
  );
};

export default ThemePackSecondaryColorComponent;
